import { Component, type ErrorInfo, type ReactNode } from 'react'
import { sessionStore } from '@features/auth/services/sessionStore'
import { Icon } from '@/icons/Icon'
import { t } from '@/i18n'

/**
 * アプリ全体の最後の砦となる Error Boundary。
 *
 * descendant の render / lifecycle で投げられた例外を捕捉し、画面全体が白く
 * 飛ぶのを防ぐ。
 *   - 例外検出時は `sessionStore.lock()` を呼び、メモリ上の SK を即 zeroize する
 *     (zero-knowledge 方針: 状態の壊れたセッションで SK を保持し続けない。
 *      unlocked 以外では no-op)。
 *   - フォールバック UI から再読み込み / ホームへ復帰できる。
 *
 * Error Boundary は class component でしか実装できない (getDerivedStateFromError /
 * componentDidCatch がフックに無いため)。i18n はフックを使わず module-level の
 * `t()` を直接呼ぶ — フォールバックは描画後に言語が変わらない静的画面なので
 * 再描画購読は不要。
 */

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    // 状態の壊れたセッションで SK を保持し続けない (unlocked のときのみ作用)。
    sessionStore.lock()
    // 診断用。秘密情報は暗号材料に隠蔽されており error message には乗らない。
    console.error('[ErrorBoundary] render error:', error, info.componentStack)
  }

  public render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div className="error-boundary" role="alert">
        <div className="error-boundary-card">
          <div className="error-boundary-icon" aria-hidden="true">
            <Icon name="alert" />
          </div>
          <h1 className="error-boundary-title">{t('common.error_boundary.title')}</h1>
          <p className="error-boundary-body">{t('common.error_boundary.body')}</p>
          <div className="error-boundary-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                window.location.hash = 'home'
                window.location.reload()
              }}
            >
              {t('common.error_boundary.home')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              {t('common.error_boundary.reload')}
            </button>
          </div>
        </div>
      </div>
    )
  }
}
